import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Megaphone, Clock, X, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const ACK_KEY = 'bonus_update_ack_v1'; // bump the suffix to re-show after edits

const fmt = (secs) => {
    if (secs <= 0) return null;
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    const s = secs % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

/**
 * One-time announcement that the old daily bonus is retired and the new
 * "Load & Get Free Play" plan is live. Dismissible (remembered), and it doubles
 * as the dashboard "next Free Play bonus" timer. Auto-hides if the promo is off.
 */
const BonusUpdateBanner = ({ onCtaClick }) => {
    const [info, setInfo] = useState(null);
    const [secs, setSecs] = useState(0);
    const [dismissed, setDismissed] = useState(() => {
        try { return localStorage.getItem(ACK_KEY) === '1'; } catch { return false; }
    });
    const tick = useRef(null);

    useEffect(() => {
        if (dismissed) return;
        let alive = true;
        axios.get(`${API}/rewards/load-bonus-info`)
            .then(({ data }) => { if (alive) { setInfo(data); setSecs(data?.seconds_remaining || 0); } })
            .catch(() => {});
        return () => { alive = false; };
    }, [dismissed]);

    useEffect(() => {
        if (secs <= 0) return;
        tick.current = setInterval(() => setSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
        return () => clearInterval(tick.current);
    }, [secs > 0]); // eslint-disable-line react-hooks/exhaustive-deps

    if (dismissed || !info || !info.enabled) return null;

    const close = () => {
        try { localStorage.setItem(ACK_KEY, '1'); } catch {}
        setDismissed(true);
    };

    const readyNow = secs <= 0;
    const countdown = fmt(secs);
    const mult = Math.round(info.wagering_multiplier || 0);
    const interval = Math.round(info.interval_hours || 0);
    const tierText = (info.tiers || [])
        .map((t) => `${t.label.replace('Load ', '')} → ${t.reward.replace(' Free Play', '')}`)
        .join('  ·  ');

    return (
        <div data-testid="bonus-update-banner" style={{
            position: 'relative', borderRadius: 18, padding: '18px 20px', marginBottom: 14, overflow: 'hidden',
            background: 'linear-gradient(105deg,#3a2a05 0%,#0d2b12 45%,#1c6b2c 100%)',
            border: '2px solid #FFD70088', color: '#fff',
            boxShadow: '0 12px 34px rgba(255,215,0,0.18)',
        }}>
            <button data-testid="bonus-update-dismiss" onClick={close} aria-label="Dismiss"
                style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(0,0,0,0.3)', border: 'none', color: '#fff', borderRadius: 999, width: 30, height: 30, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <X size={16} />
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Megaphone size={26} style={{ color: '#FFD700', flexShrink: 0 }} />
                <div style={{ fontSize: 18, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.01em', paddingRight: 30 }}>
                    Bonus update — the daily bonus has changed
                </div>
            </div>

            <p style={{ fontSize: 14, fontWeight: 700, margin: '10px 0 0', lineHeight: 1.5 }}>
                The old daily bonus is retired. Now there's something better:{' '}
                <span style={{ color: '#39FF14', fontWeight: 900 }}>Load &amp; Get Free Play</span> — the more you load,
                the bigger the bonus, available every {interval} hours.
            </p>
            {tierText && (
                <p style={{ fontSize: 13, fontWeight: 800, margin: '8px 0 0', color: '#eafff0' }}>
                    {tierText}
                </p>
            )}
            <p style={{ fontSize: 12, opacity: 0.8, margin: '4px 0 0' }}>
                Free Play · play it through {mult}× to cash out
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
                <div data-testid="bonus-update-timer" style={{
                    display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 15px', borderRadius: 999, fontWeight: 900, fontSize: 13,
                    background: readyNow ? '#39FF14' : 'rgba(0,0,0,0.35)',
                    color: readyNow ? '#052500' : '#fff', border: readyNow ? 'none' : '1px solid #FFD70055',
                }}>
                    <Clock size={15} />
                    {readyNow ? 'Your next Free Play bonus is ready now!' : `Your next Free Play bonus: ${countdown}`}
                </div>
                <button data-testid="bonus-update-cta" onClick={onCtaClick}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 999, background: '#FFD700', color: '#1a1400', fontWeight: 900, fontSize: 13, border: 'none', cursor: 'pointer' }}>
                    Load &amp; Claim <ArrowRight size={15} />
                </button>
            </div>
        </div>
    );
};

export default BonusUpdateBanner;
